import { useState } from 'react'
import {
  BAND_LABEL,
  BAND_NOTE,
  DOMAIN_LABEL,
  STATUS_LABEL,
  type Answers,
  type Assessment,
  type Band,
  type Finding,
} from '@asvise/household-engine/vocabulary'
import { FINDING, STATUS, UI } from '../lib/language'
import { Answering, unanswered, type Ask, type Asking } from './Answering'
import { AREA_INPUTS } from './areas'
import { isNamed } from './Household'
import { asksPerson } from './inputs'

/**
 * The board, and the main screen.
 *
 * The questionnaire was the way in, and a household that opened twenty
 * questions learned nothing until it finished all of them. This opens on the
 * six protection areas, each saying what it can and what it would need to say
 * more, and the questions are reached from whichever area is waiting on them.
 *
 * It works because the engine was built to answer partially rather than to be
 * fed. An area with nothing behind it comes back DATA_INCOMPLETE naming the
 * exact fields its calculation is missing, so a board can be drawn from the
 * first answer and every row can say what it needs. Nothing here decides that;
 * it reads it.
 *
 * The business app's board is the model, and the shape it borrows is the useful
 * part: bands as headings with their own note, the most pressing first, the
 * unranked last rather than first, and answering in a sheet over the top so
 * nobody loses their place. What it does not borrow is that app's rows, which
 * are fitted to zones of a business rather than to areas of a household.
 */

/** The bands, strongest first, and only those the engine can reach. */
const BANDS: Band[] = ['REVIEW_NOW', 'REVIEW_NEXT', 'MONITOR', 'NONE']

/**
 * What a band means, said where the band is.
 *
 * Framework section 8.2's thresholds are Asvise screening heuristics and the
 * framework says they must be labelled as such. A heading reading "Review now"
 * with nothing beside it is an instruction; with this it is a suggestion whose
 * basis is on the screen.
 */
const BAND_WHY: Record<Band, string> = {
  REVIEW_NOW: 'Scored highest by severity, how much is uncovered, how exposed the family is, and whether anything is time-critical.',
  REVIEW_NEXT: 'Worth looking at, and not before the ones above it.',
  MONITOR: 'Nothing pressing. Worth knowing where it stands.',
  NONE: 'Nothing in the answers given points to an action.',
}

/** Which bands get a colour of their own. The lower two read as quiet. */
const BAND_TONE: Record<Band, string> = {
  REVIEW_NOW: 'now',
  REVIEW_NEXT: 'next',
  MONITOR: '',
  NONE: '',
}

const TONE: Record<string, string> = {
  ATTENTION_REQUIRED: 'attention',
  PARTIALLY_ADDRESSED: 'partial',
  COVERAGE_NOT_VERIFIED: 'partial',
  DATA_INCOMPLETE: 'asking',
  HUMAN_REVIEW_REQUIRED: 'asking',
  NO_OBVIOUS_GAP: 'clear',
  NOT_APPLICABLE: 'clear',
}

const money = (n: number) => `S$${Math.round(n).toLocaleString('en-SG')}`

/**
 * The counts under the name: what was found, in the order the board reads.
 *
 * The business app's board names the business, says what it is, and then
 * summarises. The household had the first two (earners, dependants, buffer) and
 * not the third, so a household had to read all six rows to learn how many
 * needed looking at.
 *
 * THE SAME NUMBERS AS THE BADGES BELOW, NOT A SECOND COUNT. One per band, in
 * the engine's own words for it, over the same rows the bands hold, and an
 * unranked group last because the board puts it last. Zero is left out: "Then
 * this 0" is a heading with nothing under it.
 *
 * TWO THINGS ARE DELIBERATELY NOT HERE. A total of what is not addressed, which
 * would add a death shortfall to a critical illness shortfall: they are
 * different exposures drawn on one household's money, and a figure that
 * combined them would be read as a sum owed. And the answers a calculation is
 * missing counted per area, which would count one answer three times when three
 * areas read it. This counts distinct fields, so it says how many answers would
 * settle something, which is the number a household can act on.
 */
export function summaryOf(assessment: Assessment): { key: string; count: number; label: string }[] {
  const ranked = assessment.findings.filter((f) => !f.urgency.unscored)
  const unranked = assessment.findings.filter((f) => f.urgency.unscored)

  const byBand = BANDS.map((band) => ({
    key: band,
    count: ranked.filter((f) => f.urgency.band === band).length,
    label: BAND_LABEL[band],
  }))

  const fields = new Set(assessment.findings.flatMap((f) => f.missing))

  return [
    ...byBand,
    { key: 'UNRANKED', count: unranked.length, label: UI.notRanked ?? 'More information needed' },
    {
      key: 'ANSWERS',
      count: fields.size,
      label: fields.size === 1 ? 'answer would settle more' : 'answers would settle more',
    },
  ].filter((part) => part.count > 0)
}

/** What this area can say in one line, without the figures. */
function headline(finding: Finding): string {
  const code = finding.reason_codes[0] ?? ''
  return FINDING[code] ?? finding.evidence[0]?.trigger ?? 'Nothing was established for this.'
}

/**
 * Everything this area would ask, and who each question is about.
 *
 * From the index rather than from the finding, which is what lets a row ask
 * anything at all. The engine reports the answers a calculation was missing,
 * and four of the six areas have no calculation: a hospital policy is judged on
 * what it leaves the household to find and never produces a need, so those rows
 * offered nothing, however little was known about them. None of them could ask
 * for a member's answer either, because a member's answers are named by no
 * finding.
 *
 * The member is the one this finding is about. An area about a person asks
 * about that person: putting every member's questions on one row would ask
 * whether a two-year-old earns, on a card about their father.
 */
function asksFor(finding: Finding, answers: Answers): Ask[] {
  const inputs = AREA_INPUTS[finding.domain]
  const person = finding.person_id
    ? (answers.people.find((p) => p.id === finding.person_id) ?? null)
    : null

  return [
    ...inputs.household.map((field): Ask => ({ kind: 'HOUSEHOLD', field })),
    ...(person === null
      ? []
      : inputs.person
          // Progressive disclosure, from the question table: a share of income
          // is not asked of somebody who has not said they earn, here as on the
          // record.
          .filter((field) => asksPerson(person, field))
          .map((field): Ask => ({ kind: 'PERSON', person, field }))),
  ]
}

function Row({
  finding,
  answers,
  onAsk,
  onOpen,
}: {
  finding: Finding
  answers: Answers
  onAsk: (asking: Asking) => void
  onOpen: () => void
}) {
  const missing = finding.missing
  const tone = TONE[finding.status] ?? 'asking'
  const open = unanswered(asksFor(finding, answers), answers)

  return (
    <li className={`area-row ${tone}`}>
      <button type="button" className="area-line" onClick={onOpen}>
        <span className="area-line-head">
          <span className="area-line-name">
            {DOMAIN_LABEL[finding.domain]}
            {finding.person_label && <span className="area-line-who">{finding.person_label}</span>}
          </span>
          <span className="area-line-state">{STATUS[finding.status] ?? STATUS_LABEL[finding.status]}</span>
        </span>

        <span className="area-line-what">{headline(finding)}</span>

        {/* The one figure worth putting on a row. The rest are on the card. */}
        {finding.shortfall !== null && finding.shortfall > 0 && (
          <span className="area-line-figure">{money(finding.shortfall)} not addressed</span>
        )}
      </button>

      {/*
        The ask. Not a prompt to finish a form: it names how many answers this
        one area is still without, so somebody can settle one thing and see it
        change.

        Two wordings, because the two states are different and the stronger one
        is a claim worth keeping. Where the engine says a calculation is missing
        inputs, answering those produces a figure where there is none, and the
        row says so. Where it is not, the answers would sharpen a verdict that
        already exists, which is worth offering and is not the same promise.
      */}
      {open.length > 0 && (
        <button
          type="button"
          className="area-ask"
          onClick={() => onAsk({ title: DOMAIN_LABEL[finding.domain], asks: open })}
        >
          {missing.length > 0
            ? open.length === 1
              ? 'One answer would size this'
              : `${open.length} answers would size this`
            : open.length === 1
              ? 'One answer would sharpen this'
              : `Answer ${open.length} questions about this`}
        </button>
      )}
    </li>
  )
}

export function Board({
  assessment,
  answers,
  onChange,
  lagging,
  failed,
  onRetry,
  onOpenFinding,
  actions,
}: {
  assessment: Assessment | null
  answers: Answers
  onChange: (next: Answers) => void
  lagging: boolean
  failed: boolean
  onRetry: () => void
  /** Opens the full five-part card for one area. */
  onOpenFinding: (domain: string, person: string | null) => void
  /**
   * Where else a household can go, rendered in the header beside the summary.
   *
   * Passed in rather than built here because the routes belong to the app, and
   * placed at the top because that is where the business app puts them and
   * where somebody looks for them: at the foot of a board of eleven rows they
   * were below everything, which reads as an afterthought rather than as the
   * way out.
   */
  actions?: React.ReactNode
}) {
  const [asking, setAsking] = useState<Asking | null>(null)

  if (!assessment) {
    return failed ? (
      <section className="board">
        <p className="error">
          We could not work this out just now.{' '}
          <button type="button" className="link" onClick={onRetry}>
            Try again
          </button>
        </p>
      </section>
    ) : (
      <section className="board">
        <p className="lead">Working out what this means…</p>
      </section>
    )
  }

  const profile = assessment.household_profile
  const ranked = assessment.findings.filter((f) => !f.urgency.unscored)
  const unranked = assessment.findings.filter((f) => f.urgency.unscored)
  // Distinct answers rather than a sum, for the reason `summaryOf` gives.
  const waiting = new Set(assessment.findings.flatMap((f) => f.missing)).size

  return (
    <section className="board">
      <header className="board-head">
        <div>
          {/*
            The household's own name, the way the business app's board opens
            with the business name. It read "What this would mean for you",
            which is a description of the screen rather than a title for it: a
            board about the Tan family should say so, and the summary line
            under it already says what the screen is doing.
          */}
          {/* An account still holding the database's default name is not named, and
              its board is titled as one that is not. The default used to be "My
              household" and rows written then keep it. */}
          <h2>{isNamed(answers.household_name) ? answers.household_name.trim() : 'Your family'}</h2>
          <p className="board-context">
            {[
              profile.earner_count === null
                ? null
                : `${profile.earner_count} ${profile.earner_count === 1 ? 'earner' : 'earners'}`,
              profile.dependent_count === null
                ? null
                : `${profile.dependent_count} ${profile.dependent_count === 1 ? 'dependant' : 'dependants'}`,
              profile.emergency_buffer_months === null
                ? null
                : `${profile.emergency_buffer_months.toFixed(1)} months of buffer`,
            ]
              .filter((x) => x !== null)
              .join(' · ') || 'Tell us about the family and this fills in.'}
          </p>
          {/* Said last, after who the household is: the name identifies, the
              line above describes, and this summarises what was found. */}
          <p className="board-summary">
            {summaryOf(assessment).map((part, i) => (
              <span key={part.key} data-part={part.key}>
                {i > 0 && <span className="sep">·</span>}
                <strong>{part.count}</strong> {part.label}
              </span>
            ))}
          </p>
        </div>

        {/*
          Only once it has been behind long enough to matter. A board that
          announced every settled answer would read as broken when it is
          working.
        */}
        {lagging && <span className="board-behind">Catching up with your last answer…</span>}

        {actions && <div className="board-actions">{actions}</div>}
      </header>

      {BANDS.map((band) => {
        const rows = ranked.filter((f) => f.urgency.band === band)
        if (rows.length === 0) return null
        return (
          <div className={`band ${BAND_TONE[band]}`} key={band}>
            <h3 className="band-head">
              {BAND_LABEL[band]}
              <span className="band-count">{rows.length}</span>
            </h3>
            <p className="band-note">{BAND_WHY[band]}</p>
            <ul className="areas-list">
              {rows.map((finding) => (
                <Row
                  key={`${finding.domain}:${finding.person_id ?? ''}`}
                  finding={finding}
                  answers={answers}
                  onAsk={setAsking}
                  onOpen={() => onOpenFinding(finding.domain, finding.person_id)}
                />
              ))}
            </ul>
          </div>
        )
      })}

      {unranked.length > 0 && (
        <div className="band">
          <h3 className="band-head">
            {UI.notRanked ?? 'More information needed'}
            <span className="band-count">{unranked.length}</span>
          </h3>
          {/*
            Last rather than first, and that is not the engine dismissing them.
            It is saying what their band says: the size of the gap has not been
            established, so they cannot claim a place in a ranking. Each row
            still says exactly what is missing.
          */}
          <p className="band-note">
            How big any gap is has not been established for these, so they are not placed against
            the rest. {waiting > 0 && 'Answering what they ask for is what moves them.'}
          </p>
          <ul className="areas-list">
            {unranked.map((finding) => (
              <Row
                key={`${finding.domain}:${finding.person_id ?? ''}`}
                finding={finding}
                answers={answers}
                onAsk={setAsking}
                onOpen={() => onOpenFinding(finding.domain, finding.person_id)}
              />
            ))}
          </ul>
        </div>
      )}

      <p className="board-note">
        {assessment.order_note} {BAND_NOTE} {assessment.disclaimer}
      </p>

      {asking && (
        <Answering
          asking={asking}
          answers={answers}
          onChange={onChange}
          assessment={assessment}
          onDone={() => setAsking(null)}
        />
      )}
    </section>
  )
}
